import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Shield, Users, Briefcase, CreditCard, Activity, LogOut,
  Search, UserCheck, UserX, Trash2, Eye, DollarSign,
  TrendingUp, Clock, Loader2, RefreshCw, BarChart3, CheckCircle2, XCircle, FileCheck
} from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");

  const adminToken = localStorage.getItem("admin_token");

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${adminToken}` };
      
      const [statsRes, customersRes, prosRes, jobsRes, transRes, logsRes, verificationsRes, bgChecksRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/customers?limit=100`, { headers }),
        axios.get(`${API}/admin/professionals?limit=100`, { headers }),
        axios.get(`${API}/admin/jobs?limit=100`, { headers }),
        axios.get(`${API}/admin/transactions?limit=100`, { headers }),
        axios.get(`${API}/admin/activity-logs?limit=50`, { headers }),
        axios.get(`${API}/admin/verifications?status=pending`, { headers }),
        axios.get(`${API}/admin/background-checks`, { headers }).catch(() => ({ data: { background_checks: [] } }))
      ]);
      
      setStats(statsRes.data);
      setCustomers(customersRes.data.customers);
      setProfessionals(prosRes.data.professionals);
      setJobs(jobsRes.data.jobs);
      setTransactions(transRes.data.transactions);
      setActivityLogs(logsRes.data.logs);
      setVerifications(verificationsRes.data.verifications);
      setBackgroundChecks(bgChecksRes.data.background_checks || []);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [adminToken, navigate]);

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchAllData();
  }, [adminToken, navigate, fetchAllData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const handleSuspendUser = async (userType, userId) => {
    try {
      await axios.patch(
        `${API}/admin/users/${userType}/${userId}/suspend`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("User suspended");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to suspend user");
    }
  };

  const handleActivateUser = async (userType, userId) => {
    try {
      await axios.patch(
        `${API}/admin/users/${userType}/${userId}/activate`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("User activated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to activate user");
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;
    
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Job deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const handleApproveVerification = async (verificationId) => {
    try {
      await axios.patch(
        `${API}/admin/verifications/${verificationId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Verification approved!");
      setSelectedVerification(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to approve verification");
    }
  };

  const handleRejectVerification = async (verificationId) => {
    const notes = window.prompt("Reason for rejection (optional):");
    try {
      await axios.patch(
        `${API}/admin/verifications/${verificationId}/reject?notes=${encodeURIComponent(notes || "")}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Verification rejected");
      setSelectedVerification(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to reject verification");
    }
  };

  const handleApproveBackgroundCheck = async (checkId) => {
    try {
      await axios.patch(
        `${API}/admin/background-checks/${checkId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Background check approved!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to approve background check");
    }
  };

  const handleRejectBackgroundCheck = async (checkId) => {
    const notes = window.prompt("Reason for rejection (optional):");
    try {
      await axios.patch(
        `${API}/admin/background-checks/${checkId}/reject?notes=${encodeURIComponent(notes || "")}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Background check rejected");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to reject background check");
    }
  };

  const viewVerificationDetails = async (verificationId) => {
    try {
      const response = await axios.get(`${API}/admin/verifications/${verificationId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSelectedVerification(response.data);
    } catch (error) {
      toast.error("Failed to load verification details");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Helper function to get activity dot color
  const getActivityDotColor = (action) => {
    if (action.includes("login")) return "bg-green-500";
    if (action.includes("registration")) return "bg-blue-500";
    if (action.includes("job")) return "bg-purple-500";
    if (action.includes("bid")) return "bg-yellow-500";
    return "bg-gray-500";
  };

  // Memoized filtered data for performance
  const filteredCustomers = useMemo(() => 
    customers.filter(c => 
      c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [customers, searchQuery]
  );

  const filteredProfessionals = useMemo(() => 
    professionals.filter(p => 
      p.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.business_name?.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [professionals, searchQuery]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E40AF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Admin Header */}
      <header className="bg-[#1E40AF] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8" />
              <div>
                <h1 className="font-heading font-bold text-lg">BQL Admin</h1>
                <p className="text-xs text-blue-200">Platform Management</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchAllData}
                className="text-white hover:bg-white/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="admin-dashboard">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow">
            <TabsTrigger value="overview" className="rounded-lg">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="customers" className="rounded-lg">
              <Users className="w-4 h-4 mr-2" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="professionals" className="rounded-lg">
              <UserCheck className="w-4 h-4 mr-2" />
              Professionals
            </TabsTrigger>
            <TabsTrigger value="jobs" className="rounded-lg">
              <Briefcase className="w-4 h-4 mr-2" />
              Jobs
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-lg">
              <CreditCard className="w-4 h-4 mr-2" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="activity" className="rounded-lg">
              <Activity className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="verifications" className="rounded-lg relative">
              <Shield className="w-4 h-4 mr-2" />
              Verifications
              {verifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {verifications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="background-checks" className="rounded-lg relative">
              <FileCheck className="w-4 h-4 mr-2" />
              BG Checks
              {backgroundChecks.filter(c => c.status === "pending").length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center">
                  {backgroundChecks.filter(c => c.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg">
              <Trash2 className="w-4 h-4 mr-2" />
              Data Cleanup
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-0 shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Customers</p>
                      <p className="text-3xl font-bold text-gray-900">{stats?.users?.total_customers || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Professionals</p>
                      <p className="text-3xl font-bold text-gray-900">{stats?.users?.total_professionals || 0}</p>
                      <p className="text-xs text-green-600">{stats?.users?.active_subscriptions || 0} active</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Jobs</p>
                      <p className="text-3xl font-bold text-gray-900">{stats?.jobs?.total || 0}</p>
                      <p className="text-xs text-blue-600">{stats?.jobs?.open || 0} open</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Revenue</p>
                      <p className="text-3xl font-bold text-gray-900">${stats?.revenue?.total_revenue || 0}</p>
                      <p className="text-xs text-green-600">{stats?.revenue?.paid_transactions || 0} paid</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-yellow-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card className="border-0 shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {activityLogs.slice(0, 10).map((log) => (
                    <div key={log.id || `${log.timestamp}-${log.action}`} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${getActivityDotColor(log.action)}`} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{log.action.replace(/_/g, " ")}</p>
                          <p className="text-xs text-gray-500">{log.user_type} • {log.user_id.slice(0, 8)}...</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(log.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <Card className="border-0 shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Customers ({customers.length})</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search customers..."
                      className="pl-9"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell className="font-medium">{customer.full_name}</TableCell>
                        <TableCell>{customer.email}</TableCell>
                        <TableCell>{customer.country}</TableCell>
                        <TableCell>{formatDate(customer.created_at)}</TableCell>
                        <TableCell>
                          <Badge className={customer.status === "suspended" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                            {customer.status || "active"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {customer.status === "suspended" ? (
                            <Button size="sm" variant="outline" onClick={() => handleActivateUser("customer", customer.id)}>
                              <UserCheck className="w-4 h-4 mr-1" />
                              Activate
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleSuspendUser("customer", customer.id)}>
                              <UserX className="w-4 h-4 mr-1" />
                              Suspend
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Professionals Tab */}
          <TabsContent value="professionals">
            <Card className="border-0 shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Professionals ({professionals.length})</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search professionals..."
                      className="pl-9"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Business</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProfessionals.map((pro) => (
                      <TableRow key={pro.id}>
                        <TableCell className="font-medium">{pro.full_name}</TableCell>
                        <TableCell>{pro.business_name}</TableCell>
                        <TableCell>{pro.email}</TableCell>
                        <TableCell>
                          <Badge className={pro.subscription_status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                            {pro.subscription_status || "inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(pro.created_at)}</TableCell>
                        <TableCell>
                          {pro.status === "suspended" ? (
                            <Button size="sm" variant="outline" onClick={() => handleActivateUser("professional", pro.id)}>
                              <UserCheck className="w-4 h-4 mr-1" />
                              Activate
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleSuspendUser("professional", pro.id)}>
                              <UserX className="w-4 h-4 mr-1" />
                              Suspend
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <Card className="border-0 shadow">
              <CardHeader>
                <CardTitle>All Jobs ({jobs.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Posted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">{job.title}</TableCell>
                        <TableCell>{job.category}</TableCell>
                        <TableCell>{job.location}</TableCell>
                        <TableCell>
                          <Badge className={
                            job.status === "open" ? "bg-green-100 text-green-700" :
                            job.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                            "bg-blue-100 text-blue-700"
                          }>
                            {job.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(job.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Link to={`/job/${job.id}`} target="_blank">
                              <Button size="sm" variant="outline">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteJob(job.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card className="border-0 shadow">
              <CardHeader>
                <CardTitle>Payment Transactions ({transactions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User Email</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell>{tx.user_email}</TableCell>
                        <TableCell className="capitalize">{tx.plan_id}</TableCell>
                        <TableCell>${tx.amount}</TableCell>
                        <TableCell>
                          <Badge className={tx.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                            {tx.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(tx.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card className="border-0 shadow">
              <CardHeader>
                <CardTitle>Activity Logs ({activityLogs.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Action</TableHead>
                      <TableHead>User Type</TableHead>
                      <TableHead>User ID</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activityLogs.map((log) => (
                      <TableRow key={log.id || `${log.timestamp}-${log.action}-${log.user_id}`}>
                        <TableCell className="font-medium">{log.action.replace(/_/g, " ")}</TableCell>
                        <TableCell className="capitalize">{log.user_type}</TableCell>
                        <TableCell className="font-mono text-xs">{log.user_id.slice(0, 12)}...</TableCell>
                        <TableCell>{log.ip_address || "N/A"}</TableCell>
                        <TableCell>{formatDate(log.timestamp)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verifications Tab */}
          <TabsContent value="verifications">
            {selectedVerification ? (
              <Card className="border-0 shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Verification Review</CardTitle>
                    <Button variant="outline" onClick={() => setSelectedVerification(null)}>
                      Back to List
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Professional Info */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg">Professional Details</h3>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-gray-500">Name:</span> {selectedVerification.professional_name}</p>
                        <p><span className="text-gray-500">Email:</span> {selectedVerification.professional_email}</p>
                        <p><span className="text-gray-500">Business:</span> {selectedVerification.business_name}</p>
                        <p><span className="text-gray-500">ID Type:</span> {selectedVerification.id_type?.replace(/_/g, " ")}</p>
                        <p><span className="text-gray-500">Submitted:</span> {formatDate(selectedVerification.submitted_at)}</p>
                      </div>
                    </div>

                    {/* Photos */}
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-2">Selfie Photo</h4>
                        <img
                          src={selectedVerification.selfie_image}
                          alt="Selfie"
                          className="w-48 h-48 object-cover rounded-xl border shadow"
                        />
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">ID Document</h4>
                        <img
                          src={selectedVerification.id_document_image}
                          alt="ID Document"
                          className="max-w-[300px] max-h-[200px] object-contain rounded-xl border shadow"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 mt-8 pt-6 border-t">
                    <Button
                      onClick={() => handleApproveVerification(selectedVerification.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve Verification
                    </Button>
                    <Button
                      onClick={() => handleRejectVerification(selectedVerification.id)}
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow">
                <CardHeader>
                  <CardTitle>Pending Verifications ({verifications.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {verifications.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <p className="text-gray-600">No pending verifications</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Professional</TableHead>
                          <TableHead>Business</TableHead>
                          <TableHead>ID Type</TableHead>
                          <TableHead>Submitted</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {verifications.map((v) => (
                          <TableRow key={v.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{v.professional_name}</p>
                                <p className="text-xs text-gray-500">{v.professional_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{v.business_name}</TableCell>
                            <TableCell className="capitalize">{v.id_type?.replace(/_/g, " ")}</TableCell>
                            <TableCell>{formatDate(v.submitted_at)}</TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                onClick={() => viewVerificationDetails(v.id)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Background Checks Tab */}
          <TabsContent value="background-checks">
            <Card className="border-0 shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-purple-600" />
                  Background Check Reviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                {backgroundChecks.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No background checks to review</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Professional</TableHead>
                        <TableHead>Business</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead>Report ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {backgroundChecks.map((check) => (
                        <TableRow key={check.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{check.professional_name}</p>
                              <p className="text-xs text-gray-500">{check.professional_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{check.business_name || "-"}</TableCell>
                          <TableCell>
                            <Badge className={check.provider === "checkr" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}>
                              {check.provider === "checkr" ? "Checkr" : "Certn"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">{check.report_id}</code>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              check.status === "approved" ? "bg-green-100 text-green-700" :
                              check.status === "rejected" ? "bg-red-100 text-red-700" :
                              "bg-yellow-100 text-yellow-700"
                            }>
                              {check.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(check.submitted_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {check.status === "pending" ? (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApproveBackgroundCheck(check.id)}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-300 text-red-600 hover:bg-red-50"
                                  onClick={() => handleRejectBackgroundCheck(check.id)}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">
                                {check.status === "approved" ? "Verified" : "Rejected"}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Cleanup Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="border-0 shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="w-5 h-5" />
                  Data Cleanup
                </CardTitle>
                <p className="text-sm text-gray-500">Remove test data from the database. This action cannot be undone.</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Clear Test Leads */}
                <div className="border rounded-lg p-4 bg-red-50">
                  <h3 className="font-semibold text-gray-900 mb-2">Clear All Leads & Jobs</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This will delete ALL jobs, lead assignments, job bids, and lead views from the database.
                  </p>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm("Are you sure you want to delete ALL leads and jobs? This cannot be undone.")) return;
                      try {
                        const token = localStorage.getItem("adminToken");
                        const response = await axios.delete(`${API}/admin/clear-test-data`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success(`Cleared: ${response.data.results.jobs_deleted} jobs, ${response.data.results.lead_assignments_deleted} assignments`);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to clear data");
                      }
                    }}
                    data-testid="clear-leads-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear All Leads
                  </Button>
                </div>

                {/* Clear Test Users */}
                <div className="border rounded-lg p-4 bg-yellow-50">
                  <h3 className="font-semibold text-gray-900 mb-2">Clear Test Users</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This will delete users with emails containing &quot;test&quot; or &quot;@example.com&quot;. Real user accounts will NOT be affected.
                  </p>
                  <Button
                    variant="outline"
                    className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
                    onClick={async () => {
                      if (!window.confirm("Are you sure you want to delete test users? This cannot be undone.")) return;
                      try {
                        const token = localStorage.getItem("adminToken");
                        const response = await axios.delete(`${API}/admin/clear-test-users`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success(`Cleared: ${response.data.results.test_customers_deleted} test customers, ${response.data.results.test_professionals_deleted} test professionals`);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to clear test users");
                      }
                    }}
                    data-testid="clear-test-users-btn"
                  >
                    <UserX className="w-4 h-4 mr-2" />
                    Clear Test Users
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
